<template>
  <ValidationObserver v-slot="{ handleSubmit }">
    <b-modal
      id="configEndpointSchemasModal"
      v-model="modal.show"
      :title="`Edit '${modal.data.endpointName}' Endpoint`"
      :hide-header-close="!allowedToCloseModal"
      :no-close-on-backdrop="!allowedToCloseModal"
      :no-close-on-esc="!allowedToCloseModal"
      @ok.prevent="handleSubmit(onSubmit)"
      @hidden="handleHideModal"
      @show="onShowModal"
    >
      <content-spinner
        v-if="isLoading"
        :show="isLoading || modal.loading"
      />
      <show-error
        :show="showError"
        :text="modalError"
      />

      <b-form
        v-if="!modal.loading && !isLoading"
        @submit.prevent="handleSubmit(onSubmit)"
        @keydown.enter.prevent="handleSubmit(onSubmit)"
      >
        <div
          v-for="field in salesforceNewDefaultFields"
          :class="{'d-flex align-items-center justify-content-between': field.name === 'entity'}"
        >
          <input-widget
            :id="`addEditEndpoint-${field.name}-field`"
            :key="field.columnName"
            :model="form"
            :field="field"
            :class="{'w-75': field.name === 'entity'}"
            :autofocus="field.autofocus"
            :disabled="field.disabled || isEdit && field.name === 'entityName'"
          />
          <div
            v-if="field.name === 'entity'"
            style="margin-top: 1rem; color: dodgerblue; cursor: pointer; font-weight: bold;"
            class="nav-link pl-0"
            @click="goToEntity"
          >
            Go To Entity
          </div>
        </div>

        <div
          v-if="filteredSchemasItems"
          class="d-flex align-items-start justify-content-between"
        >
          <div><b>Configure API Column Objects:</b></div>
          <div class="d-flex flex-column align-items-end">
            <div class="search-input">
              <b-form style="height: 34px;">
                <b-form-group>
                  <b-input-group>
                    <input-widget
                      :id="`searchInput`"
                      :field="searchInput"
                      :model="filter"
                      @input="search"
                    />
                    <b-button
                      v-if="filter.keyword"
                      id="clearSearchBtn"
                      variant="transparent"
                      class="transparent search-clear-btn"
                      style="margin-left: -36px; z-index: 100;"
                      @click="resetSearchData"
                    >
                      <font-awesome-icon :icon="['fas', 'times']" />
                    </b-button>
                  </b-input-group>
                </b-form-group>
              </b-form>
            </div>
            <div
              v-if="filteredSchemasItems.length"
              style="margin-right: 60px;"
              class="mt-1 mb-1"
            >
              Read
            </div>
          </div>
        </div>

        <b-card
          v-if="filteredSchemasItems"
          no-body
        >
          <no-content
            :show="!filteredSchemasItems.length"
            text="Endpoints not exist"
          />

          <b-card-body class="endpoint-content p-0 pt-2">
            <div class="columns-block">
              <div
                v-for="card in filteredSchemasItems"
                :key="card.key"
                class="pt-1 pl-3 pr-3 pb-1 d-flex align-items-center justify-content-between schemas-item"
              >
                <div class="d-flex align-items-center col-6 pl-0">
                  <b>{{ card.key }}</b>
                </div>

                <div class="new-salesforce-inputs ml-auto">
                  <b-form-checkbox
                    v-model="card.isReadEnabled"
                    @change="(val) => onReadWriteStatusChange({name: 'isReadEnabled', columnName: card.key, value: val})"
                  />
                </div>

                <b-button
                  class="ml-1 action-button"
                  variant="outline-primary"
                  data-toggle="tooltip"
                  data-placement="bottom"
                  size="sm"
                  title="Edit"
                  @click="onEditClick(card.key)"
                >
                  <font-awesome-icon :icon="['fas', 'edit']" />
                </b-button>
              </div>
            </div>
          </b-card-body>
        </b-card>

        <b-form-checkbox
          v-model="isOnlyConfiguredShow"
          class="mt-2 ml-1"
        >
          Show only configured
        </b-form-checkbox>
      </b-form>
      <div
        slot="modal-footer"
        class="d-flex"
      >
        <div
          id="modal-footer-close"
          class="d-flex"
        >
          <b-button
            id="addEditEndpointModalCancelBtn"
            class="mt-3 mr-3"
            block
            :disabled="!allowedToCloseModal"
            @click="$bvModal.hide('configEndpointSchemasModal')"
          >
            Close
          </b-button>
          <b-tooltip
            v-if="!allowedToCloseModal"
            target="modal-footer-close"
            placement="top"
          >
            Please mark at least one column for 'Read' and save it before closing
          </b-tooltip>
        </div>

        <div id="modal-footer-save">
          <b-button
            id="addEditEndpointModalOkBtn"
            class="mt-3"
            variant="primary"
            block
            :disabled="!allowedToSave"
            @click.prevent="handleSubmit(onSubmit);"
          >
            Save
          </b-button>
          <b-tooltip
            v-if="!allowedToSave"
            target="modal-footer-save"
            placement="top"
          >
            Please mark at least one column for 'Read' before saving
          </b-tooltip>
        </div>
      </div>

      <endpoint-config-form-modal
        ref="schemaConfigModal"
        :schema-name="selectedSchemaName"
        :schema="selectedSchema"
        @onHideModal="onHideSchemaModal"
      />
    </b-modal>
  </ValidationObserver>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import ContentSpinner from '../../../core/components/ContentSpinner';
import InputWidget from '../../../core/components/complex-table/components/InputWidget';
import ShowError from '../../../core/components/ShowError';
import DataManagementModal from '../../../core/components/complex-table/DataManagementModal';
import EndpointConfigForm from '@/modules/ConnectorScreen/modals/EndpointConfigFormModal.vue';
import EndpointConfigFormModal from '@/modules/ConnectorScreen/modals/EndpointConfigFormModal.vue';
import NoContent from '@/core/components/NoContent.vue';

const {mapState, mapActions} = createNamespacedHelpers('connectorScreen');

const {
  mapState: mapDataManagementState,
  mapActions: mapDataManagementActions,
} = createNamespacedHelpers('dataManagement');

export default {
  name: 'EndpointConfigModal',
  components: {
    NoContent,
    EndpointConfigFormModal,
    EndpointConfigForm,
    ShowError,
    InputWidget,
    ContentSpinner,
    DataManagementModal,
  },
  data() {
    return {
      showError: false,
      isDisable: false,
      modalError: '',
      form: {},
      salesforceNewDefaultFields: {
        bulkAPIChunkSize: {
          type: 'number',
          label: 'Bulk API Chunk Size',
          name: 'bulkAPIChunkSize',
        },
        jobCheckDelayInMilliseconds: {
          type: 'number',
          label: 'Job Check Delay In Milliseconds',
          name: 'jobCheckDelayInMilliseconds',
        },
        recordThresholdForBulkAPI: {
          type: 'number',
          label: 'Record Threshold For Bulk API',
          name: 'recordThresholdForBulkAPI',
        },
        endpointId: {
          type: 'text',
          label: 'Endpoint Id',
          name: 'endpointId',
          disabled: true,
        },
        endpointName: {
          type: 'text',
          label: 'Endpoint Name',
          name: 'endpointName',
          disabled: true,
        },
        entity: {
          type: 'text',
          label: 'Entity',
          name: 'entity',
          disabled: true,
        },
      },
      selectedSchemaName: '',
      isShowSchemaModal: false,
      filter: {
        keyword: '',
      },
      searchInput: {
        type: 'text',
        label: false,
        placeholder: 'Search',
        name: 'keyword',
      },
      schemasItems: [],
      isOnlyConfiguredShow: false,
      backupSchemasItems: [],
    };
  },
  watch: {
    getEntityName(val) {
      this.form.entity = val;
    },
  },
  computed: {
    ...mapDataManagementState({
      tables: state => state.tables,
    }),
    ...mapState({
      modal: state => state.endpoint.modal,
      connector: state => state.connector,
      isLoading: state => state.loading,
    }),
    isEdit() {
      return this.modal.data && Object.keys(this.modal.data).length > 2;
    },
    schemasCards() {
      if (this.modal.data.fieldSchemas) {
        return this.modal.data.fieldSchemas
          .map((fs, idx) => {
            let isLast;
            idx === this.modal.data.fieldSchemas.length - 1 ? isLast = true : isLast = false;

            return ({
              key: fs.columnName,
              name: fs.columnName,
              dataType: fs.dataType,
              collapsed: true, isLast,
              isReadEnabled: fs.isReadEnabled,
              isWriteEnabled: fs.isWriteEnabled,
            });
          })
          .sort((a, b) => {
            const valA = a.isReadEnabled || a.isWriteEnabled ? 1 : 0;
            const valB = b.isReadEnabled || b.isWriteEnabled ? 1 : 0;
            return valA > valB ? -1 : 1;
          });
      }
    },
    selectedSchema() {
      if (this.form.fieldSchemas) {
        return this.form.fieldSchemas.find(sc => sc.columnName === this.selectedSchemaName);
      }
      return {};
    },
    getEntityName() {
      if (this.tables.data.length) {
        const searchedEntity = this.tables.data
          .find(e => e.entityGuid === this.form.entityGuid);
        return searchedEntity ? searchedEntity.displayName : '';
      }
      return '';
    },
    getModuleGuid() {
      if (this.tables.data) {
        const searchedEntity = this.tables.data
          .find(e => e.entityGuid === this.form.entityGuid);
        return searchedEntity ? searchedEntity.moduleGuid : '';
      }
      return '';
    },
    filteredSchemasItems() {
      if (this.isOnlyConfiguredShow) {
        return this.schemasItems.filter(i => i.isReadEnabled || i.isWriteEnabled);
      }
      return this.schemasItems;
    },
    allowedToCloseModal() {
      return this.backupSchemasItems.some(i => i.isReadEnabled);
    },
    allowedToSave() {
      return this.schemasItems.some(i => i.isReadEnabled);
    },
  },
  methods: {
    ...mapActions(['hideEndpointModal', 'saveEndpoint', 'updateSalesforceEndpoint']),
    ...mapDataManagementActions(['getTables']),
    async onSubmit() {
      this.isDisable = true;

      const {success, body} = await this.updateSalesforceEndpoint({
        configurationId: this.$route.params.configurationId,
        body: {...this.form, isReadEnabled: true},
      });
      if (success) {
        this.$toasted.success('Endpoint updated successfully', {
          duration: TOASTER_DURATION,
        });
        this.getTables();
        this.handleHideModal();
        this.$emit('onEndpointsUpdate', false);
      } else {
        this.isDisable = false;
        this.showError = true;
        this.modalError = body.ErrorMessage;
      }
    },
    handleHideModal() {
      this.form = {};
      this.isDisable = false;
      this.showError = false;
      this.modalError = '';
      this.filter.keyword = '';
      this.hideEndpointModal();
    },
    onShowModal() {
      if (this.isEdit) {
        this.form = {...this.modal.data, entity: this.getEntityName};
        this.schemasItems = [...this.schemasCards];
      }

      if (this.schemasItems) {
        this.backupSchemasItems = JSON.parse(JSON.stringify(this.schemasItems));
      }
    },
    // onCollapseClick(card) {
    //   card.collapsed = !card.collapsed;
    //   this.$root.$emit('bv::toggle::collapse', card.key);
    // },
    onEditClick(schemasName) {
      this.selectedSchemaName = schemasName;
      this.$refs.schemaConfigModal.$refs.schemaConfigModal.show();
    },
    onHideSchemaModal(schema) {
      const schemaToUpdateName = schema.columnName;
      this.form.fieldSchemas = this.form.fieldSchemas.map(fsc => {
        if (fsc.columnName === schemaToUpdateName) {
          return schema;
        } else {
          return fsc;
        }
      });

      this.selectedSchemaName = '';
    },
    onReadWriteStatusChange(val) {
      this.form.fieldSchemas.map(field => {
        if (field.columnName === val.columnName) {
          field[val.name] = val.value;
          return field;
        } else {
          return field;
        }
      });
    },
    resetSearchData() {
      this.schemasItems = [...this.schemasCards];
      this.filter.keyword = '';
    },
    search(keyword) {
      if (!keyword) return this.resetSearchData();

      this.schemasItems = this.schemasCards.filter(item => {
        return item.name.toLowerCase().trim().indexOf(keyword.toLowerCase().trim()) > -1;
      });
    },
    goToEntity() {
      const entityGUID = this.form.entityGuid;
      const separator = this.$router.mode === 'hash' ? '#' : '';
      const url = `${separator}/app/all-modules/${this.getModuleGuid}/table/${entityGUID}`;
      window.open(url, '_blank', 'noreferrer');
    },
  },
  mounted() {
    if (this.schemasCards) {
      this.schemasItems = [...this.schemasCards];
    }
  },
};
</script>

<style scoped lang="scss">
.schemas-item {
  transition: background-color .3s ease-in-out;
  &:hover {
    background-color: rgba(128, 128, 128, 0.1);
  }
}

.columns-block {
  max-height: 225px;
  overflow: auto;
}
</style>
